package com.arena_web.ui.sections.clubdetail.mobile

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.ClubDetailUiState
import com.arena_web.data.dto.ClubDto
import com.arena_web.data.dto.ClubGalleryImageDto
import com.arena_web.data.dto.ClubReviewDto
import com.arena_web.data.dto.WorkingHoursDto
import com.arena_web.pages.clubDistanceKm
import com.arena_web.pages.clubSportLabel
import com.arena_web.pages.formatClubKm
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.arena_web.utils.openInMaps
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color as KobwebColor
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * Mobile club-detail screen — replicates the KMP app's ClubDetailScreen
 * (shared/presentation/screen/clubdetail) exactly: 312-px hero photo with
 * rounded bottom, 44-px back/heart circles over it, glass rating chip +
 * sport chip riding the photo edge, the navy info card straddling the photo
 * bottom, then the Photos | Amenities | Map segmented tabs.
 *
 * The root sets `--scale: var(--mobile-scale)` so every `s()`-scaled value
 * renders against the app's 390-px baseline (same trick as
 * EventDetailContentMobile / BottomSheet).
 */
@Composable
fun ClubDetailContentMobile(
    state: ClubDetailUiState,
    userCoords: Pair<Double, Double>?,
    onBack: () -> Unit,
    onRate: (Int) -> Unit,
) {
    val t = LocalAppStrings.current

    Column(
        modifier = Modifier
            .fillMaxSize()
            .styleModifier {
                property("--scale", "var(--mobile-scale)")
                property("min-height", "0")
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
            }
    ) {
        when {
            state.isLoading -> {
                FloatingBackButton(onBack)
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center,
                ) { /* Intentionally empty — no loading indicator (user pref). */ }
            }
            state.error != null || state.club == null -> {
                FloatingBackButton(onBack)
                Box(
                    modifier = Modifier.fillMaxWidth().flexGrow(1),
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        state.error ?: t.pageSomethingWentWrong,
                        modifier = Modifier.sFontSize(16).color(com.arena_web.ui.theme.ThemeVars.Text2),
                    )
                }
            }
            else -> {
                val club = state.club!!
                ClubHeroHeader(
                    club = club,
                    workingHours = state.schedule?.workingHours.orEmpty(),
                    userCoords = userCoords,
                    reviews = state.reviews,
                    isSubmittingReview = state.isSubmittingReview,
                    onBack = onBack,
                    onRate = onRate,
                )
                Box(modifier = Modifier.sHeight(20).styleModifier { property("flex-shrink", "0") })
                ClubMobileTabs(
                    club = club,
                    gallery = state.gallery,
                    amenities = state.amenities,
                )
                // Trailing spacer — padding-bottom on a flex scroll container is
                // dropped at scroll-end on Chrome/Safari.
                Box(modifier = Modifier.sHeight(32).styleModifier { property("flex-shrink", "0") })
            }
        }
    }
}

// ── Hero: photo + overlay buttons + chips + info card (app HeaderGallery) ──

/** App constant: chips/info-card column starts this far down the 312 photo. */
private const val HEADER_CHIP_ROW_TOP = 162
private const val HEADER_PHOTO_HEIGHT = 312

@Composable
private fun ClubHeroHeader(
    club: ClubDto,
    workingHours: List<WorkingHoursDto>,
    userCoords: Pair<Double, Double>?,
    reviews: List<ClubReviewDto>,
    isSubmittingReview: Boolean,
    onBack: () -> Unit,
    onRate: (Int) -> Unit,
) {
    var favorite by remember(club.id) {
        mutableStateOf(favoriteClubIds().contains(club.id))
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("position", "relative")
                property("flex-shrink", "0")
                property("min-height", s(HEADER_PHOTO_HEIGHT))
            }
    ) {
        // Hero photo — full-bleed, rounded bottom corners (app: 16dp).
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(HEADER_PHOTO_HEIGHT)
                .styleModifier {
                    property("position", "absolute")
                    property("top", "0")
                    property("left", "0")
                    property("border-radius", "0 0 ${s(16)} ${s(16)}")
                    property("overflow", "hidden")
                    property(
                        "background",
                        "linear-gradient(180deg, rgba(60, 121, 241, 0.06) 0%, rgba(5, 4, 12, 0.06) 100%)"
                    )
                }
        ) {
            if (!club.mainPhotoUrl.isNullOrBlank()) {
                Image(
                    src = club.mainPhotoUrl!!,
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property("object-fit", "cover")
                    }
                )
            }
        }

        // Back + favorite — 44 navy circles over the photo (app HeaderRoundButton).
        Row(
            modifier = Modifier
                .styleModifier {
                    property("position", "absolute")
                    property("top", "calc(env(safe-area-inset-top, 0px) + ${s(8)})")
                    property("left", s(16))
                    property("right", s(16))
                    property("z-index", "3")
                    property("justify-content", "space-between")
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            HeaderRoundButton(onClick = onBack) {
                Icon(
                    src = AppIcons.ChevronForward,
                    modifier = Modifier.sSize(18).styleModifier {
                        property("transform", "rotate(180deg)")
                    }
                )
            }
            HeaderRoundButton(onClick = {
                favorite = !favorite
                setFavoriteClub(club.id, favorite)
            }) {
                if (favorite) {
                    // Filled red heart when favorited (app HeartLikedRed).
                    Box(
                        modifier = Modifier.sSize(24).styleModifier {
                            property("background-color", "#FF4D67")
                            property("mask-image", "url('${AppIcons.Heart}')")
                            property("-webkit-mask-image", "url('${AppIcons.Heart}')")
                            property("mask-size", "contain")
                            property("-webkit-mask-size", "contain")
                            property("mask-repeat", "no-repeat")
                            property("-webkit-mask-repeat", "no-repeat")
                            property("mask-position", "center")
                            property("-webkit-mask-position", "center")
                        }
                    )
                } else {
                    Icon(src = AppIcons.HeartOutline, modifier = Modifier.sSize(24))
                }
            }
        }

        // Chips + info card ride the photo's bottom edge (app: top = 162).
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sPaddingLeft(16).sPaddingRight(16)
                .styleModifier {
                    property("position", "relative")
                    property("z-index", "2")
                    property("padding-top", s(HEADER_CHIP_ROW_TOP))
                    property("box-sizing", "border-box")
                }
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                RatingChip(
                    club = club,
                    reviews = reviews,
                    isSubmittingReview = isSubmittingReview,
                    onRate = onRate,
                )
                SportChip(club)
            }
            Box(modifier = Modifier.sHeight(16))
            ClubInfoCardMobile(
                club = club,
                workingHours = workingHours,
                userCoords = userCoords,
            )
        }
    }
}

@Composable
private fun HeaderRoundButton(onClick: () -> Unit, content: @Composable () -> Unit) {
    Box(
        modifier = Modifier
            .sSize(44)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
                property("background", "#07122C")
                property("flex-shrink", "0")
            },
        contentAlignment = Alignment.Center,
    ) {
        GradientBorderRing(radiusPx = 100)
        content()
    }
}

/** Back circle for the loading/error states (no hero photo to overlay). */
@Composable
private fun FloatingBackButton(onBack: () -> Unit) {
    Box(
        modifier = Modifier.sPadding(8, 16).styleModifier {
            property("padding-top", "calc(env(safe-area-inset-top, 0px) + ${s(8)})")
        }
    ) {
        HeaderRoundButton(onClick = onBack) {
            Icon(
                src = AppIcons.ChevronForward,
                modifier = Modifier.sSize(18).styleModifier {
                    property("transform", "rotate(180deg)")
                }
            )
        }
    }
}

// ── Rating chip + stars dropdown (app glass badge / RateClubDropdown) ──

@Composable
private fun RatingChip(
    club: ClubDto,
    reviews: List<ClubReviewDto>,
    isSubmittingReview: Boolean,
    onRate: (Int) -> Unit,
) {
    val isLoggedIn = Storage.get(StorageKeys.ACCESS_TOKEN) != null
    val myUserId = Storage.get(StorageKeys.USER_ID)
    // Ratings can only go up (backend upserts one review per user).
    val myRating = reviews.firstOrNull { it.userId == myUserId }
        ?.rating?.let { kotlin.math.round(it).toInt() } ?: 0
    var showDropdown by remember { mutableStateOf(false) }

    Box(modifier = Modifier.styleModifier { property("position", "relative") }) {
        Row(
            modifier = Modifier
                .sBorderRadius(12)
                .sPaddingLeft(6).sPaddingRight(6)
                .sPaddingTop(4).sPaddingBottom(4)
                .sGap(4)
                .thenIf(isLoggedIn) {
                    Modifier.cursor(Cursor.Pointer).onClick { showDropdown = !showDropdown }
                }
                .styleModifier {
                    property("position", "relative")
                    property("background", "rgba(0, 0, 0, 0.5)")
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            GradientBorderRing(radiusPx = 12)
            Icon(src = AppIcons.StarRating16, modifier = Modifier.sSize(16))
            SpanText(
                club.averageRating.asDynamic().toFixed(1) as String,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Medium)
                    .color(KobwebColor.hex("BED6F5"))
            )
            if (club.totalRatingsCount > 0) {
                SpanText(
                    "(${club.totalRatingsCount})",
                    modifier = Modifier
                        .sFontSize(12)
                        .fontWeight(AppTypography.Light)
                        .color(KobwebColor.hex("99B1BF"))
                )
            }
        }
        if (showDropdown) {
            Row(
                modifier = Modifier
                    .sBorderRadius(20)
                    .sPaddingLeft(14).sPaddingRight(14)
                    .sPaddingTop(10).sPaddingBottom(10)
                    .sGap(8)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "calc(100% + ${s(6)})")
                        property("left", "0")
                        property("z-index", "10")
                        property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                        property("border", "1px solid rgba(41, 52, 138, 0.4)")
                        property("box-shadow", "0 ${s(10)} ${s(30)} rgba(0, 0, 0, 0.45)")
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                repeat(5) { index ->
                    val starIndex = index + 1
                    // Stars below the previous vote render but aren't tappable.
                    val allowed = starIndex >= myRating && !isSubmittingReview
                    Icon(
                        src = AppIcons.StarRating16,
                        modifier = Modifier
                            .sSize(24)
                            .thenIf(allowed) {
                                Modifier.cursor(Cursor.Pointer).onClick {
                                    it.stopPropagation()
                                    onRate(starIndex)
                                    showDropdown = false
                                }
                            }
                            .styleModifier {
                                if (starIndex > myRating) {
                                    property("filter", "grayscale(1) opacity(0.35)")
                                }
                            }
                    )
                }
            }
        }
    }
}

@Composable
private fun SportChip(club: ClubDto) {
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .sHeight(24)
            .sBorderRadius(12)
            .sPaddingLeft(12).sPaddingRight(12)
            .backgroundColor(sportColors.primary)
            .styleModifier {
                property("border", "0.3px solid var(--arena-border-top)")
                property("box-sizing", "border-box")
                property("flex-shrink", "0")
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            clubSportLabel(club),
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Medium)
                .color(sportColors.onPrimary)
                .styleModifier { property("white-space", "nowrap") }
        )
    }
}

// ── Info card (app ClubInfoCard): navy 22-radius card, blue glow shadow ──

@Composable
private fun ClubInfoCardMobile(
    club: ClubDto,
    workingHours: List<WorkingHoursDto>,
    userCoords: Pair<Double, Double>?,
) {
    val t = LocalAppStrings.current
    val status = openStatus(workingHours)
    val distanceKm = userCoords?.let { (uLat, uLng) ->
        clubDistanceKm(uLat, uLng, club.latitude, club.longitude)
    }
    var scheduleExpanded by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(22)
            .sPaddingLeft(12).sPaddingRight(12)
            .sPaddingTop(12).sPaddingBottom(22)
            .sGap(18)
            .styleModifier {
                property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                property("box-shadow", "0 ${s(6)} ${s(24)} rgba(60, 121, 241, 0.35)")
                property("box-sizing", "border-box")
            }
    ) {
        // Title + address
        Column(modifier = Modifier.sGap(8).fillMaxWidth().styleModifier { property("min-width", "0") }) {
            SpanText(
                club.name,
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(22)
                    .sPaddingLeft(3)
                    .color(AppColors.PrimaryWhite)
                    .styleModifier {
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                    }
            )
            Row(
                modifier = Modifier
                    .sGap(4)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        val maps = club.googleMapsUrl?.takeIf { it.isNotBlank() }
                        if (maps != null) {
                            window.open(withHttpsPrefix(maps), "_blank")
                        } else {
                            openInMaps(
                                club.latitude?.toString(),
                                club.longitude?.toString(),
                                club.address,
                            )
                        }
                    }
                    .styleModifier { property("min-width", "0") },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    src = AppIcons.Location,
                    modifier = Modifier.sSize(16).styleModifier {
                        property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                        property("flex-shrink", "0")
                    }
                )
                SpanText(
                    club.address,
                    modifier = Modifier
                        .sFontSize(14)
                        .color(KobwebColor.hex("BED6F5"))
                        .styleModifier {
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                )
            }
        }

        // Open-now (tap → inline weekly schedule) + distance chips.
        if (status != null || distanceKm != null) {
            Row(modifier = Modifier.sGap(12).styleModifier { property("flex-wrap", "wrap") }) {
                status?.let { st ->
                    InfoChipMobile(
                        onClick = if (workingHours.isNotEmpty()) {
                            { scheduleExpanded = !scheduleExpanded }
                        } else null
                    ) {
                        Box(
                            modifier = Modifier.sSize(6).sBorderRadius(100).styleModifier {
                                property(
                                    "background",
                                    if (st.isOpen) "#00E5A4" else "rgba(190, 214, 245, 0.4)"
                                )
                                property("flex-shrink", "0")
                            }
                        )
                        val label = when {
                            st.isOpen && st.time != null ->
                                "${t.clubOpenLabel} · ${t.clubUntilTime(st.time)}"
                            st.isOpen -> t.clubOpenLabel
                            st.time != null -> "${t.clubClosed} · ${t.clubOpensAt(st.time)}"
                            else -> t.clubClosed
                        }
                        SpanText(
                            label,
                            modifier = Modifier
                                .sFontSize(16)
                                .fontWeight(AppTypography.Medium)
                                .color(KobwebColor.hex("BED6F5"))
                                .styleModifier { property("white-space", "nowrap") }
                        )
                    }
                }
                distanceKm?.let { km ->
                    InfoChipMobile(onClick = null) {
                        Icon(
                            src = AppIcons.Location,
                            modifier = Modifier.sSize(16).styleModifier {
                                property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                                property("flex-shrink", "0")
                            }
                        )
                        SpanText(
                            t.clubKmAway(formatClubKm(km)),
                            modifier = Modifier
                                .sFontSize(16)
                                .fontWeight(AppTypography.Medium)
                                .color(KobwebColor.hex("BED6F5"))
                                .styleModifier { property("white-space", "nowrap") }
                        )
                    }
                }
            }
        }

        // Weekly schedule — expands inline inside the card (app parity),
        // today highlighted with the sport colour, full day names.
        if (scheduleExpanded && workingHours.isNotEmpty()) {
            WeeklyScheduleCard(workingHours)
        }

        // Contact actions — call, instagram, website, google, google-maps.
        val phone = club.phone?.takeIf { it.isNotBlank() }
        val instagram = club.instagramUrl?.takeIf { it.isNotBlank() }
        val website = club.websiteUrl?.takeIf { it.isNotBlank() }
        val googleSearch = club.googleSearchUrl?.takeIf { it.isNotBlank() }
        val googleMaps = club.googleMapsUrl?.takeIf { it.isNotBlank() }
        if (phone != null || instagram != null || website != null ||
            googleSearch != null || googleMaps != null
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().sGap(16),
                horizontalArrangement = Arrangement.Center,
            ) {
                if (phone != null) {
                    ClubActionCircleMobile(AppIcons.CallFilled, 24) {
                        window.open("tel:" + phone.filter { !it.isWhitespace() }, "_self")
                    }
                }
                if (instagram != null) {
                    ClubActionCircleMobile(AppIcons.Instagram, 21) {
                        window.open(withHttpsPrefix(instagram), "_blank")
                    }
                }
                if (website != null) {
                    ClubActionCircleMobile(AppIcons.GlobeFilled, 24) {
                        window.open(withHttpsPrefix(website), "_blank")
                    }
                }
                if (googleSearch != null) {
                    ClubActionCircleMobile(AppIcons.Google, 21) {
                        window.open(withHttpsPrefix(googleSearch), "_blank")
                    }
                }
                if (googleMaps != null) {
                    ClubActionCircleMobile(AppIcons.GoogleMaps, 22) {
                        window.open(withHttpsPrefix(googleMaps), "_blank")
                    }
                }
            }
        }
    }
}

/** 40-tall gradient pill chip (app InfoChip). */
@Composable
private fun InfoChipMobile(onClick: (() -> Unit)?, content: @Composable () -> Unit) {
    Row(
        modifier = Modifier
            .sHeight(40)
            .sBorderRadius(20)
            .sPaddingLeft(14).sPaddingRight(14)
            .sGap(6)
            .thenIf(onClick != null) {
                Modifier.cursor(Cursor.Pointer).onClick { onClick?.invoke() }
            }
            .styleModifier {
                property("position", "relative")
                property("background", AppGradients.InteractiveSecondary)
                property("box-sizing", "border-box")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        GradientBorderRing(radiusPx = 20)
        content()
    }
}

@Composable
private fun WeeklyScheduleCard(workingHours: List<WorkingHoursDto>) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val todayIso = currentIsoDay()
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sBorderRadius(14)
            .sPaddingLeft(14).sPaddingRight(14)
            .sPaddingTop(12).sPaddingBottom(12)
            .sGap(8)
            .styleModifier {
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property(
                    "background",
                    "linear-gradient(180deg, rgba(60, 121, 241, 0.096) 0%, rgba(5, 4, 12, 0.09) 100%)"
                )
                property("box-sizing", "border-box")
            }
    ) {
        workingHours.sortedBy { it.dayOfWeek }.forEach { day ->
            val isToday = day.dayOfWeek == todayIso
            Row(
                modifier = Modifier.fillMaxWidth().sGap(16),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    localizedWeekdayFull(day.dayOfWeek),
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(if (isToday) AppTypography.Medium else AppTypography.Regular)
                        .styleModifier {
                            property(
                                "color",
                                if (isToday) sportColors.primary.toString() else "#BED6F5"
                            )
                        }
                )
                SpanText(
                    if (day.isClosed) t.clubClosed
                    else "${day.openTime.take(5)} – ${day.closeTime.take(5)}",
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Medium)
                        .styleModifier {
                            property(
                                "color",
                                when {
                                    day.isClosed -> "var(--arena-text-3)"
                                    isToday -> sportColors.primary.toString()
                                    else -> "#B4CCF6"
                                }
                            )
                            property("white-space", "nowrap")
                        }
                )
            }
        }
    }
}

/** 44-circle contact action (app ClubRoundAction). */
@Composable
private fun ClubActionCircleMobile(iconSrc: String, iconPx: Int, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .sSize(44)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
                property("background", AppGradients.InteractiveSecondary)
                property("flex-shrink", "0")
                property("box-sizing", "border-box")
            },
        contentAlignment = Alignment.Center,
    ) {
        GradientBorderRing(radiusPx = 100)
        Icon(src = iconSrc, modifier = Modifier.sSize(iconPx))
    }
}

// ── Tabs: Photos | Amenities | Map (app ClubTabsSection) ──

private enum class ClubMobileTab { Photos, Amenities, Map }

private data class MobileAmenity(val type: String, val icon: String)

private val MOBILE_AMENITIES = listOf(
    MobileAmenity("PARKING", AppIcons.Parking),
    MobileAmenity("CAFE", AppIcons.Cafe),
    MobileAmenity("WIFI", AppIcons.Wifi),
    MobileAmenity("RECEPTION", AppIcons.Reception),
    MobileAmenity("SHOWER", AppIcons.Shower),
    MobileAmenity("FIRST_AID", AppIcons.FirstAid),
)

@Composable
private fun ClubMobileTabs(
    club: ClubDto,
    gallery: List<ClubGalleryImageDto>,
    amenities: List<String>,
) {
    val t = LocalAppStrings.current
    var activeTab by remember { mutableStateOf(ClubMobileTab.Photos) }
    // Tapped gallery photo shown in a fullscreen overlay (app ImageViewerDialog).
    var viewerUrl by remember { mutableStateOf<String?>(null) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .sPaddingLeft(16).sPaddingRight(16)
            .sGap(16)
            .styleModifier {
                property("flex-shrink", "0")
                property("box-sizing", "border-box")
            }
    ) {
        TabSwitcher(activeTab) { activeTab = it }
        when (activeTab) {
            ClubMobileTab.Photos -> GallerySectionMobile(gallery) { viewerUrl = it }
            ClubMobileTab.Amenities -> AmenitiesSectionMobile(amenities)
            ClubMobileTab.Map -> ClubMapSectionMobile(club)
        }
    }

    if (viewerUrl != null) {
        Box(
            modifier = Modifier
                .cursor(Cursor.Pointer)
                .onClick { viewerUrl = null }
                .styleModifier {
                    property("position", "fixed")
                    property("inset", "0")
                    property("z-index", "2000")
                    property("background", "rgba(0, 0, 0, 0.92)")
                },
            contentAlignment = Alignment.Center,
        ) {
            Image(
                src = viewerUrl!!,
                modifier = Modifier.styleModifier {
                    property("max-width", "100%")
                    property("max-height", "100%")
                    property("object-fit", "contain")
                }
            )
        }
    }
}

@Composable
private fun TabSwitcher(active: ClubMobileTab, onSelect: (ClubMobileTab) -> Unit) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(44)
            .sBorderRadius(22)
            .sPadding(4)
            .styleModifier {
                property("position", "relative")
                property("background", AppGradients.InteractiveSecondary)
                property("box-sizing", "border-box")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        GradientBorderRing(radiusPx = 22)
        ClubMobileTab.entries.forEach { tab ->
            val isActive = tab == active
            val label = when (tab) {
                ClubMobileTab.Photos -> t.clubTabPhotos
                ClubMobileTab.Amenities -> t.pageAmenities
                ClubMobileTab.Map -> t.pageMap
            }
            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .sBorderRadius(18)
                    .cursor(Cursor.Pointer)
                    .onClick { onSelect(tab) }
                    .thenIf(isActive) { Modifier.backgroundColor(sportColors.primary) }
                    .styleModifier {
                        property("flex", "1")
                        property("min-width", "0")
                    },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    label,
                    modifier = Modifier
                        .sFontSize(16)
                        .fontWeight(AppTypography.Medium)
                        .color(if (isActive) sportColors.onPrimary else KobwebColor.hex("BED6F5"))
                        .styleModifier {
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                )
            }
        }
    }
}

// ── Photos tab: lead image full-width, then 2-per-row (app GallerySection) ──

@Composable
private fun GallerySectionMobile(
    gallery: List<ClubGalleryImageDto>,
    onOpen: (String) -> Unit,
) {
    val t = LocalAppStrings.current
    if (gallery.isEmpty()) {
        TabEmptyState(icon = AppIcons.Camera, text = t.clubNoPhotos)
        return
    }
    Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
        GalleryTile(
            imageUrl = gallery[0].imageUrl,
            modifier = Modifier.fillMaxWidth(),
            onOpen = onOpen,
        )
        gallery.drop(1).chunked(2).forEach { row ->
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("gap", s(12))
                    property("width", "100%")
                }
            }) {
                row.forEach { image ->
                    GalleryTile(
                        imageUrl = image.imageUrl,
                        modifier = Modifier.styleModifier {
                            property("flex", "1")
                            property("min-width", "0")
                        },
                        onOpen = onOpen,
                    )
                }
                if (row.size == 1) {
                    Box(modifier = Modifier.styleModifier {
                        property("flex", "1")
                        property("min-width", "0")
                    })
                }
            }
        }
    }
}

@Composable
private fun GalleryTile(
    imageUrl: String,
    modifier: Modifier,
    onOpen: (String) -> Unit,
) {
    Box(
        modifier = modifier
            .sHeight(162)
            .sBorderRadius(16)
            .cursor(Cursor.Pointer)
            .onClick { onOpen(imageUrl) }
            .styleModifier {
                property("position", "relative")
                property("overflow", "hidden")
                property(
                    "background",
                    "linear-gradient(190deg, rgba(60, 121, 241, 0.06) 49%, rgba(5, 4, 12, 0.06) 106%)"
                )
            },
        contentAlignment = Alignment.Center,
    ) {
        GradientBorderRing(radiusPx = 16, borderWidthPx = 1)
        Image(
            src = imageUrl,
            modifier = Modifier.fillMaxSize().styleModifier {
                property("object-fit", "cover")
            }
        )
    }
}

// ── Amenities tab: 2-per-row 56-tall chips (app AmenitiesSection) ──

@Composable
private fun AmenitiesSectionMobile(amenities: List<String>) {
    val t = LocalAppStrings.current
    val clubAmenities = MOBILE_AMENITIES.filter { it.type in amenities }
    if (clubAmenities.isEmpty()) {
        TabEmptyState(icon = AppIcons.Reception, text = t.clubNoAmenities)
        return
    }
    Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
        clubAmenities.chunked(2).forEach { row ->
            Div(attrs = {
                style {
                    property("display", "flex")
                    property("gap", s(12))
                    property("width", "100%")
                }
            }) {
                row.forEach { info -> AmenityChipMobile(info) }
                if (row.size == 1) {
                    Box(modifier = Modifier.styleModifier {
                        property("flex", "1")
                        property("min-width", "0")
                    })
                }
            }
        }
    }
}

@Composable
private fun AmenityChipMobile(info: MobileAmenity) {
    val t = LocalAppStrings.current
    val label = when (info.type) {
        "PARKING" -> t.pageAmenityParking
        "CAFE" -> t.pageAmenityCafe
        "WIFI" -> t.pageAmenityWifi
        "RECEPTION" -> t.pageAmenityReception
        "SHOWER" -> t.pageAmenityShower
        else -> t.pageAmenityFirstAid
    }
    Box(
        modifier = Modifier
            .sHeight(56)
            .sBorderRadius(14)
            .styleModifier {
                property("flex", "1")
                property("min-width", "0")
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property(
                    "background",
                    "linear-gradient(180deg, rgba(60, 121, 241, 0.096) 0%, rgba(5, 4, 12, 0.09) 100%)"
                )
                property("box-sizing", "border-box")
            },
        contentAlignment = Alignment.Center,
    ) {
        Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
            Icon(src = info.icon, modifier = Modifier.sSize(16))
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
    }
}

/** Shared empty state for the Photos/Amenities tabs (app ClubTabEmptyState). */
@Composable
private fun TabEmptyState(icon: String, text: String) {
    Column(
        modifier = Modifier.fillMaxWidth().sHeight(162).sGap(10),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier = Modifier
                .sSize(56)
                .sBorderRadius(100)
                .styleModifier {
                    property("position", "relative")
                    property("background", AppGradients.InteractiveSecondary)
                },
            contentAlignment = Alignment.Center,
        ) {
            GradientBorderRing(radiusPx = 100)
            Icon(src = icon, modifier = Modifier.sSize(24))
        }
        SpanText(
            text,
            modifier = Modifier.sFontSize(14).color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}

// ── Map tab: 216-tall rounded Leaflet map (app ClubMapSection) ──

@Composable
private fun ClubMapSectionMobile(club: ClubDto) {
    val t = LocalAppStrings.current
    val lat = club.latitude
    val lng = club.longitude

    if (lat == null || lng == null) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(216)
                .sBorderRadius(16)
                .sGap(8)
                .styleModifier { property("background", "var(--arena-surface-variant)") },
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Icon(
                src = AppIcons.Map,
                modifier = Modifier.sSize(32).styleModifier { property("opacity", "0.6") }
            )
            SpanText(
                t.clubMapComingSoon,
                modifier = Modifier.sFontSize(12).color(com.arena_web.ui.theme.ThemeVars.Text3)
            )
        }
        return
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(216)
            .sBorderRadius(16)
            .styleModifier {
                property("overflow", "hidden")
                property("position", "relative")
            }
    ) {
        Div(attrs = {
            id("club-detail-map-mobile")
            style {
                property("width", "100%")
                property("height", "100%")
            }
        })

        LaunchedEffect(lat, lng) {
            delay(200)
            window.asDynamic().__clubDetailMapMobileLat = lat
            window.asDynamic().__clubDetailMapMobileLng = lng

            js("""
                (function() {
                    var id = 'club-detail-map-mobile';
                    var lat = window.__clubDetailMapMobileLat;
                    var lng = window.__clubDetailMapMobileLng;
                    var container = document.getElementById(id);
                    if (!container) return;
                    if (typeof L === 'undefined') return;

                    if (window.__clubDetailMapMobile) {
                        try { window.__clubDetailMapMobile.remove(); } catch(e) {}
                        window.__clubDetailMapMobile = null;
                    }

                    var map = L.map(id, {
                        zoomControl: false,
                        attributionControl: false,
                        scrollWheelZoom: false,
                        dragging: false,
                        doubleClickZoom: false,
                        touchZoom: false,
                        boxZoom: false
                    }).setView([lat, lng], 15);

                    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                        maxZoom: 20,
                        subdomains: 'abcd'
                    }).addTo(map);

                    var markerIcon = L.divIcon({
                        className: '',
                        html: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="26" height="35">' +
                            '<path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20C24 5.373 18.627 0 12 0z" fill="#0061FF"/>' +
                            '<circle cx="12" cy="11" r="5" fill="white"/>' +
                            '</svg>',
                        iconSize: [26, 35],
                        iconAnchor: [13, 35]
                    });

                    L.marker([lat, lng], { icon: markerIcon }).addTo(map);

                    window.__clubDetailMapMobile = map;
                    setTimeout(function() { map.invalidateSize(); }, 150);
                })();
            """)
        }

        DisposableEffect(Unit) {
            onDispose {
                js("""
                    (function() {
                        if (window.__clubDetailMapMobile) {
                            try { window.__clubDetailMapMobile.remove(); } catch(e) {}
                            window.__clubDetailMapMobile = null;
                        }
                    })();
                """)
            }
        }
    }
}

// ── Local helpers (parity copies of pages/clubs/Id.kt privates) ──

/** Whether the club is open right now; [time] is the closing time when open,
 *  today's opening time when it opens later today, null otherwise. */
private data class OpenStatusInfo(val isOpen: Boolean, val time: String?)

private fun openStatus(workingHours: List<WorkingHoursDto>): OpenStatusInfo? {
    if (workingHours.isEmpty()) return null
    val now = kotlin.js.Date()
    // JS Date.getDay(): 0 = Sunday … 6 = Saturday → ISO 1 = Monday … 7 = Sunday.
    val isoDay = ((now.getDay() + 6) % 7) + 1
    val hhmm = now.getHours().toString().padStart(2, '0') + ":" +
        now.getMinutes().toString().padStart(2, '0')
    val today = workingHours.firstOrNull { it.dayOfWeek == isoDay }
    if (today != null && !today.isClosed) {
        val open = today.openTime.take(5)
        val close = today.closeTime.take(5)
        if (hhmm >= open && hhmm < close) return OpenStatusInfo(isOpen = true, time = close)
        if (hhmm < open) return OpenStatusInfo(isOpen = false, time = open)
    }
    return OpenStatusInfo(isOpen = false, time = null)
}

private fun currentIsoDay(): Int = ((kotlin.js.Date().getDay() + 6) % 7) + 1

/** Localized FULL weekday name for an ISO day (1 = Mon) via Intl — the app's
 *  schedule dropdown spells days out (Monday…), unlike the desktop's short form. */
private fun localizedWeekdayFull(isoDay: Int): String {
    val lang = Storage.get(StorageKeys.LANGUAGE)?.lowercase() ?: "en"
    val locale = if (lang == "ua") "uk" else lang
    // 2024-01-01 was a Monday.
    val d = kotlin.js.Date(kotlin.js.Date.UTC(2024, 0, isoDay))
    return d.asDynamic().toLocaleDateString(locale, js("({ weekday: 'long', timeZone: 'UTC' })")) as String
}

/** Device-local favorite clubs (comma-separated ids in Storage) — same list
 *  the desktop club-page heart writes. */
private fun favoriteClubIds(): Set<String> =
    Storage.get(StorageKeys.FAVORITE_CLUB_IDS)
        ?.split(",")?.filter { it.isNotBlank() }?.toSet().orEmpty()

private fun setFavoriteClub(id: String, favorite: Boolean) {
    val ids = favoriteClubIds().toMutableSet()
    if (favorite) ids.add(id) else ids.remove(id)
    Storage.save(StorageKeys.FAVORITE_CLUB_IDS, ids.joinToString(","))
}

private fun withHttpsPrefix(url: String): String =
    if (url.startsWith("http://") || url.startsWith("https://")) url else "https://$url"
